import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString();
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(4)}`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({
    total: 0,
    waiting: 0,
    received: 0,
    expired: 0,
    refunded: 0,
    totalSales: 0,
    totalRefunds: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingOrderId, setDeletingOrderId] = useState("");

  const loadOrders = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders`);
      const payload = await response.json();

      if (!response.ok) {
        setError(payload.message || "Failed to load orders.");
        return;
      }

      setOrders(Array.isArray(payload.data) ? payload.data : []);
      setTotals(payload.totals || {
        total: 0,
        waiting: 0,
        received: 0,
        expired: 0,
        refunded: 0,
        totalSales: 0,
        totalRefunds: 0,
      });
    } catch (_error) {
      setError("Unable to connect to backend service.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleDeleteOrder = async (order) => {
    const approved = window.confirm(
      `Delete order for ${order.userEmail || "user"} (${order.phoneNumber || "no phone"})?`
    );
    if (!approved) {
      return;
    }

    const orderId = String(order.id || "").trim();
    if (!orderId) {
      setError("Order id is missing.");
      return;
    }

    setDeletingOrderId(orderId);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
        method: "DELETE",
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload.message || "Failed to delete order.");
        return;
      }

      await loadOrders();
    } catch (_error) {
      setError("Unable to connect to backend service.");
    } finally {
      setDeletingOrderId("");
    }
  };

  const filteredOrders = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return orders.filter((order) => {
      if (statusFilter !== "all" && String(order.status || "") !== statusFilter) {
        return false;
      }

      if (!normalizedQuery) return true;

      const haystack = [
        order.userEmail,
        order.providerAlias,
        order.serviceName,
        order.serviceCode,
        order.country,
        order.phoneNumber,
        order.providerTransactionId,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");

      return haystack.includes(normalizedQuery);
    });
  }, [orders, query, statusFilter]);

  return (
    <section className="admin-page-card">
      <h2>Orders</h2>
      <p>Complete order visibility across all users, providers, lifecycle states, and refunds.</p>

      <div className="orders-summary-grid">
        <article className="orders-summary-card">
          <span>Total Orders</span>
          <strong>{totals.total || 0}</strong>
        </article>
        <article className="orders-summary-card">
          <span>Waiting</span>
          <strong>{totals.waiting || 0}</strong>
        </article>
        <article className="orders-summary-card">
          <span>Received OTP</span>
          <strong>{totals.received || 0}</strong>
        </article>
        <article className="orders-summary-card">
          <span>Expired</span>
          <strong>{totals.expired || 0}</strong>
        </article>
        <article className="orders-summary-card">
          <span>Refunded</span>
          <strong>{totals.refunded || 0}</strong>
        </article>
        <article className="orders-summary-card">
          <span>Net Sales</span>
          <strong>{formatMoney(Number(totals.totalSales || 0) - Number(totals.totalRefunds || 0))}</strong>
        </article>
      </div>

      <section className="orders-panel" aria-label="Orders controls and listing">
        <div className="orders-panel-header">
          <h3>All User Orders</h3>
          <button type="button" className="row-btn row-btn-muted" onClick={loadOrders}>
            Refresh
          </button>
        </div>

        <div className="orders-filters">
          <input
            type="text"
            className="orders-search-input"
            placeholder="Search by email, phone, service, provider, transaction id"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <select
            className="orders-status-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="waiting">waiting</option>
            <option value="received">received</option>
            <option value="expired">expired</option>
          </select>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}

        <div className="orders-table-wrap">
          <table className="orders-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Provider</th>
                <th>Service</th>
                <th>Country</th>
                <th>Phone</th>
                <th>OTP</th>
                <th>Status</th>
                <th>Selling</th>
                <th>Base</th>
                <th>Markup</th>
                <th>Refund</th>
                <th>Transaction</th>
                <th>Expires</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15" className="orders-empty-row">Loading orders...</td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="15" className="orders-empty-row">No orders found for current filters.</td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.userEmail || "N/A"}</td>
                    <td>{order.providerDisplayName || order.providerAlias || "N/A"}</td>
                    <td>
                      <div className="orders-service-cell">
                        <strong>{order.serviceName || "N/A"}</strong>
                        <span>{order.serviceCode || "N/A"}</span>
                      </div>
                    </td>
                    <td>{order.country || order.assignedCountry || "N/A"}</td>
                    <td>{order.phoneNumber || "N/A"}</td>
                    <td>{order.otpCode || "Waiting"}</td>
                    <td>
                      <span
                        className={
                          order.status === "received"
                            ? "status-badge status-active"
                            : order.status === "expired"
                              ? "status-badge status-suspended"
                              : "status-badge orders-status-waiting"
                        }
                      >
                        {order.status || "waiting"}
                      </span>
                    </td>
                    <td>{formatMoney(order.sellingPrice)}</td>
                    <td>{formatMoney(order.providerBasePrice)}</td>
                    <td>{Number(order.markupPercentage || 0).toFixed(2)}%</td>
                    <td>
                      {order.refundedAt
                        ? `${formatMoney(order.refundAmount)} (${formatDateTime(order.refundedAt)})`
                        : "Not refunded"}
                    </td>
                    <td>{order.providerTransactionId || "N/A"}</td>
                    <td>{formatDateTime(order.expiresAt)}</td>
                    <td>{formatDateTime(order.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="row-btn row-btn-danger"
                        onClick={() => handleDeleteOrder(order)}
                        disabled={deletingOrderId === order.id}
                      >
                        {deletingOrderId === order.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="orders-cards-grid">
          {filteredOrders.map((order) => (
            <article key={`${order.id}-card`} className="orders-mobile-card">
              <div className="orders-mobile-head">
                <h4>{order.serviceName || "Unknown Service"}</h4>
                <span
                  className={
                    order.status === "received"
                      ? "status-badge status-active"
                      : order.status === "expired"
                        ? "status-badge status-suspended"
                        : "status-badge orders-status-waiting"
                  }
                >
                  {order.status || "waiting"}
                </span>
              </div>

              <div className="orders-mobile-grid">
                <p><strong>User:</strong> {order.userEmail || "N/A"}</p>
                <p><strong>Provider:</strong> {order.providerDisplayName || order.providerAlias || "N/A"}</p>
                <p><strong>Country:</strong> {order.country || order.assignedCountry || "N/A"}</p>
                <p><strong>Phone:</strong> {order.phoneNumber || "N/A"}</p>
                <p><strong>OTP:</strong> {order.otpCode || "Waiting"}</p>
                <p><strong>Selling:</strong> {formatMoney(order.sellingPrice)}</p>
                <p><strong>Base:</strong> {formatMoney(order.providerBasePrice)}</p>
                <p><strong>Markup:</strong> {Number(order.markupPercentage || 0).toFixed(2)}%</p>
                <p><strong>Tx ID:</strong> {order.providerTransactionId || "N/A"}</p>
                <p><strong>Expires:</strong> {formatDateTime(order.expiresAt)}</p>
                <p><strong>Created:</strong> {formatDateTime(order.createdAt)}</p>
                <p>
                  <strong>Refund:</strong>{" "}
                  {order.refundedAt
                    ? `${formatMoney(order.refundAmount)} on ${formatDateTime(order.refundedAt)}`
                    : "Not refunded"}
                </p>
                <button
                  type="button"
                  className="row-btn row-btn-danger"
                  onClick={() => handleDeleteOrder(order)}
                  disabled={deletingOrderId === order.id}
                >
                  {deletingOrderId === order.id ? "Deleting..." : "Delete Order"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};

export default OrdersPage;
